import { useState, useEffect, useCallback } from "react";
import { Star, X, RefreshCw, CheckCircle2, Users, Building2, Award } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  lender:     { label: "Lender",     icon: "💰", color: "#0A84FF" },
  contractor: { label: "Contractor", icon: "🔧", color: "#FF9F0A" },
  realtor:    { label: "Realtor",    icon: "🏠", color: "#30D158" },
  title:      { label: "Title Co",  icon: "📄", color: "#BF5AF2" },
  investor:   { label: "Co-Investor",icon: "📈", color: "#D4AF37" },
};

const CATEGORIES = [
  { value: "communication",  label: "Communication" },
  { value: "reliability",    label: "Reliability" },
  { value: "expertise",      label: "Expertise" },
  { value: "deal_quality",   label: "Deal Execution" },
  { value: "general",        label: "Overall" },
];

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          className="transition-transform hover:scale-110"
        >
          <Star
            className="w-6 h-6"
            fill={n <= (hovered || value) ? "#D4AF37" : "none"}
            stroke={n <= (hovered || value) ? "#D4AF37" : "#3A3A3C"}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

function ParticipantReviewCard({ participant, dealId, onReviewed }) {
  const [rating, setRating]     = useState(0);
  const [comment, setComment]   = useState("");
  const [category, setCategory] = useState("general");
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);
  const [skipped, setSkipped]   = useState(false);

  const roleCfg = ROLE_LABELS[participant.role] || ROLE_LABELS.investor;
  const displayName = participant.role_label || (ROLE_LABELS[participant.role]?.label) || participant.role;

  if (done) {
    return (
      <div className="p-4 border border-[#00FF88]/20 bg-[#00FF88]/[0.04] rounded-xl flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-[#00FF88] flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-white/70">{participant.name}</p>
          <p className="text-xs text-[#00FF88]">Review submitted — thanks!</p>
        </div>
      </div>
    );
  }

  if (skipped) {
    return (
      <div className="p-4 border border-white/[0.06] bg-white/[0.02] rounded-xl flex items-center gap-3 opacity-40">
        <X className="w-4 h-4 text-white/20 flex-shrink-0" />
        <p className="text-sm text-white/30">{participant.name} — Skipped</p>
      </div>
    );
  }

  const submit = async () => {
    if (rating === 0) return toast.error("Select a star rating first");
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/reputation/review`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reviewed_user_id: participant.user_id,
          rating,
          comment: comment.trim() || null,
          category,
          deal_id: dealId,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        if (err.detail?.includes("already reviewed")) {
          toast.info("Already reviewed this partner for this deal");
          setDone(true);
          onReviewed();
          return;
        }
        throw new Error(err.detail);
      }
      toast.success(`Review for ${participant.name} submitted!`);
      setDone(true);
      onReviewed();
    } catch (e) {
      toast.error(e.message || "Failed to submit review");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 border border-white/[0.08] bg-white/[0.02] rounded-xl space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">{roleCfg.icon}</span>
          <div>
            <p className="text-sm font-semibold text-white">{participant.name}</p>
            <p className="text-[11px] font-medium" style={{ color: roleCfg.color }}>{displayName}</p>
          </div>
        </div>
        <button onClick={() => setSkipped(true)} className="text-[10px] text-white/20 hover:text-white/40 transition-colors">
          Skip
        </button>
      </div>

      <StarRating value={rating} onChange={setRating} />

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
          >
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value} style={{ background: "#111" }}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Quick note (opt)</label>
          <input
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Great partner..."
            className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-2 py-1.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
          />
        </div>
      </div>

      <button
        onClick={submit}
        disabled={saving || rating === 0}
        data-testid={`submit-review-${participant.user_id}`}
        className="w-full flex items-center justify-center gap-2 py-2 bg-[#D4AF37] text-black font-bold text-sm rounded-xl disabled:opacity-40 transition-all"
      >
        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" fill="currentColor" />}
        Submit Review
      </button>
    </div>
  );
}

export default function PostCloseReviewModal({ dealId, dealAddress, onClose }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [allDone, setAllDone]           = useState(false);

  const fetchParticipants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/deals/${dealId}/participants`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      // Exclude the deal owner (investor role is often the current user)
      const others = (data.participants || []).filter(p => p.role !== "investor" && p.user_id);
      setParticipants(others);
    } catch {}
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { fetchParticipants(); }, [fetchParticipants]);

  const handleReviewed = () => {
    const newCount = reviewedCount + 1;
    setReviewedCount(newCount);
    if (newCount >= participants.length) setAllDone(true);
  };

  const reviewableParticipants = participants.filter(p => p.user_id);
  const isEmpty = !loading && reviewableParticipants.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#0F0F0F] border border-white/[0.1] rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
              <Award className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="text-white font-bold">Deal Closed!</h3>
              <p className="text-xs text-white/40">{dealAddress || "Rate your deal partners"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/20 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Congrats banner */}
          <div className="p-4 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-xl text-center">
            <p className="text-[#D4AF37] font-bold text-sm">Congratulations on closing!</p>
            <p className="text-white/40 text-xs mt-1">
              {isEmpty
                ? "No partners to review for this deal yet."
                : "Rate your partners below to build the DealForge reputation graph and help the community."
              }
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-6">
              <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
            </div>
          ) : isEmpty ? (
            <div className="text-center py-6">
              <Users className="w-8 h-8 text-white/10 mx-auto mb-2" />
              <p className="text-white/30 text-sm">No linked partners to review.</p>
              <p className="text-white/20 text-xs mt-1">Add partners to future deals to unlock reviews.</p>
            </div>
          ) : allDone ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-10 h-10 text-[#00FF88] mx-auto mb-3" />
              <p className="text-white font-bold">All reviews submitted!</p>
              <p className="text-white/40 text-xs mt-1">Your ratings help the whole DealForge network.</p>
            </div>
          ) : (
            reviewableParticipants.map(p => (
              <ParticipantReviewCard
                key={p.user_id}
                participant={p}
                dealId={dealId}
                onReviewed={handleReviewed}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            data-testid="close-review-modal"
            className="w-full py-2.5 border border-white/[0.08] text-white/50 hover:text-white text-sm rounded-xl transition-all"
          >
            {allDone || isEmpty ? "Done" : "Skip for now"}
          </button>
        </div>
      </div>
    </div>
  );
}
